import moment from "moment";
import {
  DailyTime,
  DropListItem,
  FieldsPerUnit,
  SetLockedRows,
} from "../models";
import { DEFAULT_UNIT_FIELDS } from "./constant";
import { parseDelimitedValues } from "./commonFunctions";

export const getValidSelectedLocations = (
  selectedLocations: string[] | string | null | undefined,
  locations: DropListItem[],
): string[] => {
  if (!selectedLocations) return [];
  const selectedArr = Array.isArray(selectedLocations)
    ? selectedLocations
    : parseDelimitedValues(selectedLocations);
  return selectedArr.filter((val) =>
    locations.some((loc) => loc.value === val),
  );
};

export const getFullNotes = (payas?: string, note?: string | null) => {
  return ((payas || "") + (note ? " " + note : "")).trim();
};

export const assignUnitFields = (
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  unitEntry: Record<string, any>,
  // eslint-disable-next-line  @typescript-eslint/no-explicit-any
  apiDay: Record<string, any>,
  fullNotes: string,
  fieldMap?: Record<string, string>,
): void => {
  Object.keys(DEFAULT_UNIT_FIELDS).forEach((field) => {
    const targetField = fieldMap?.[field] || field;
    if (field === "CamCall") unitEntry[targetField] = apiDay.CamCall || "00:00";
    else if (field === "CamWrap")
      unitEntry[targetField] = apiDay.CamWrap || "00:00";
    else if (field === "customNote") unitEntry[targetField] = fullNotes;
    else {
      const value = apiDay[field];
      if (value !== undefined) {
        unitEntry[targetField] = value;
      }
    }
  });
};

export const handleMealBreakLockedRows = (
  extraFields: FieldsPerUnit,
  rowIndex: number,
  setMealBreakLockedRows?: SetLockedRows,
) => {
  const mealBreaks: [string | undefined, string | undefined][] = [
    [extraFields.StartMeal, extraFields.FinishMeal1],
    [extraFields.StartMeal2, extraFields.FinishMeal2],
    [extraFields.StartMeal3, extraFields.FinishMeal3],
  ];

  const isEmptyTime = (value?: string) => {
    if (!value) {
      return true;
    }

    const trimmed = value.trim();

    if (trimmed === "00:00" || trimmed === "00:00:00" || trimmed === "0:00") {
      return true;
    }

    // Plain time string
    const timeRegex = /^\d{1,2}:\d{2}(:\d{2})?$/;
    if (timeRegex.test(trimmed)) {
      const timeMoment = moment(
        trimmed,
        ["HH:mm", "H:mm", "HH:mm:ss", "H:mm:ss"],
        true,
      );

      return !timeMoment.isValid();
    }

    // ISO datetime / normal datetime
    const dateMoment = moment(trimmed);

    return !dateMoment.isValid();
  };

  const updateLockedRow = (locked: 0 | 1) => {
    setMealBreakLockedRows?.((prevRows) => {
      const updatedRows = [...prevRows];
      updatedRows[rowIndex] = locked;
      return updatedRows;
    });

    return locked;
  };

  const allEmpty = mealBreaks.every(
    ([start, finish]) => isEmptyTime(start) && isEmptyTime(finish),
  );

  if (allEmpty) {
    return updateLockedRow(0);
  }

  let totalMealTimeMs = 0;

  mealBreaks.forEach(([start, finish]) => {
    if (isEmptyTime(start) || isEmptyTime(finish)) {
      return;
    }

    const startMoment = moment(start);
    const finishMoment = moment(finish);

    if (!startMoment.isValid() || !finishMoment.isValid()) {
      return;
    }

    // Handle overnight meal break
    if (finishMoment.isBefore(startMoment)) {
      finishMoment.add(1, "day");
    }

    const diff = finishMoment.diff(startMoment);

    if (diff > 0) {
      totalMealTimeMs += diff;
    }
  });

  return updateLockedRow(totalMealTimeMs >= 0 ? 1 : 0);
};

export const getLockedRowsArrays = (
  rows: DailyTime[] = [],
): { mealBreakLockedRowsArr: number[]; payAsLockedRowsArr: number[] } => {
  const mealBreakLockedRowsArr: number[] = Array(rows.length).fill(0);
  const payAsLockedRowsArr: number[] = Array(rows.length).fill(0);
  rows.forEach((row, index) => {
    mealBreakLockedRowsArr[index] = handleMealBreakLockedRows(
      row as FieldsPerUnit,
      index,
    );
    payAsLockedRowsArr[index] = row.Unit && row.Payas !== "0" ? 1 : 0;
  });
  return { mealBreakLockedRowsArr, payAsLockedRowsArr };
};

export const setLockedRowsAndWeekData = (
  rows: DailyTime[],
  setMealBreakLockedRows: (rows: number[]) => void,
  setPayAsLockedRows: (rows: number[]) => void,
  setWeekData: (rows: DailyTime[]) => void,
  resetPayAsLockedRows = false,
) => {
  const { mealBreakLockedRowsArr, payAsLockedRowsArr } =
    getLockedRowsArrays(rows);
  setMealBreakLockedRows(mealBreakLockedRowsArr);
  setPayAsLockedRows(
    resetPayAsLockedRows ? Array(rows.length).fill(0) : payAsLockedRowsArr,
  );
  setWeekData(rows);
};
